import { X } from "lucide-react";
import { Modal } from "./UI";

function formatTs(ts) {
  if (!ts) return "Not available";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "Not available";
  return d.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function pct(value, total) {
  if (!total) return "0%";
  return `${Math.round((value / total) * 100)}%`;
}

function formatValue(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    return Number(value).toLocaleString();
  }
  return value ?? "—";
}

function StatSquareCard({ label, value, total, color, icon, showPct = true }) {
  const numeric = typeof value === "number" && Number.isFinite(value);
  const shareLabel = numeric && total && showPct ? pct(value, total) : null;

  return (
    <div className="rounded-lg border border-slate-100 bg-white shadow-sm px-2 py-2 flex flex-col items-center justify-center text-center gap-1 min-w-0 min-h-[72px]">
      {icon && (
        <div
          className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
          style={{ background: `${color || "#64748b"}18` }}
        >
          {icon}
        </div>
      )}
      <p className="text-base font-bold text-slate-900 leading-none tabular-nums">
        {formatValue(value)}
      </p>
      <p className="text-[9px] font-medium text-slate-500 leading-tight line-clamp-2 px-0.5">
        {label}
      </p>
      {shareLabel && (
        <p className="text-[8px] font-semibold text-slate-400">{shareLabel}</p>
      )}
    </div>
  );
}

export default function IntegrationStatsDetailModal({
  open,
  onClose,
  config,
  loading = false,
}) {
  if (!config) return null;

  const { title, subtitle, icon, accent, stats, rows, meta = [], badge } = config;

  return (
    <Modal open={open} onClose={onClose} bare zIndex={200} align="top" size="lg">
      <div
        className="w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
        style={{ marginTop: "2vh" }}
      >
        <div
          className="px-6 py-5 flex items-start justify-between gap-4"
          style={{ background: accent?.headerBg || "linear-gradient(135deg, #eef2ff, #f8fafc)" }}
        >
          <div className="flex items-start gap-3 min-w-0">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
              style={{ background: accent?.iconBg || "#6366f1", color: "#fff" }}
            >
              {icon}
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                {title}
                {badge}
              </h2>
              <p className="text-sm text-slate-600 mt-0.5">{subtitle}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center text-slate-500 hover:bg-slate-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 animate-pulse">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="min-h-[72px] bg-slate-100 rounded-lg" />
              ))}
            </div>
          ) : !stats?.hasData && !stats?.alwaysShow ? (
            <div className="text-center py-14 px-4">
              <p className="text-slate-500 text-sm">No data received yet.</p>
              <p className="text-slate-400 text-xs mt-2">
                Stats will appear after the external system pushes data to this tenant.
              </p>
            </div>
          ) : (
            <>
              <div
                className="rounded-2xl p-5 mb-5 text-center border"
                style={{
                  background: accent?.summaryBg || "#f8fafc",
                  borderColor: accent?.summaryBorder || "#e2e8f0",
                }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  {stats.summaryLabel || "Total"}
                </p>
                <p
                  className="text-4xl font-bold mt-1"
                  style={{ color: accent?.summaryText || "#0f172a" }}
                >
                  {Number(stats.total || 0).toLocaleString()}
                </p>
                {stats.updatedAt && (
                  <p className="text-xs text-slate-500 mt-2">
                    Last updated: {formatTs(stats.updatedAt)}
                  </p>
                )}
              </div>

              {meta.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mb-4">
                  {meta.map((m) => (
                    <StatSquareCard
                      key={m.label}
                      label={m.label}
                      value={m.value}
                      total={0}
                      showPct={false}
                    />
                  ))}
                </div>
              )}

              {rows.length > 0 && (
                <>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">
                    Breakdown
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {rows.map((row) => (
                      <StatSquareCard
                        key={row.label}
                        {...row}
                        total={row.barTotal ?? stats.barTotal ?? stats.total}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
